import type {
  Chain, ChainStep, DailyStatus, DropReviewItem, DropReviewList, DropReviewSaveResult, MorningSummary, ReviewState, RiseReason,
} from '../types/daily';

// 아침 서버 체인 · 오늘 상태 · 서버 검토 응답 읽기(감사 card-controltower#566, 백엔드 PR #78 · #79). 모양이 다르면 null —
// 화면은 예전 방식(브라우저 사슬 · localStorage)으로 돌아간다.

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

function normalizeStep(raw: unknown): ChainStep | null {
  const s = obj(raw);
  if (!s || typeof s.step !== 'string') return null;
  return {
    step:         s.step,
    label:        str(s.label) ?? s.step,
    status:       str(s.status) ?? 'RUNNING',
    jobId:        str(s.jobId),
    startedAt:    str(s.startedAt),
    finishedAt:   str(s.finishedAt),
    total:        num(s.total),
    successCount: num(s.successCount),
    failCount:    num(s.failCount),
    skippedCount: num(s.skippedCount),
    warn:         s.warn === true,
    message:      str(s.message),
    topReasons:   Array.isArray(s.topReasons)
      ? s.topReasons.map(obj).filter((r): r is Obj => !!r && typeof r.reason === 'string')
        .map((r) => ({ reason: r.reason as string, count: num(r.count) }))
      : [],
  };
}

export function normalizeChain(raw: unknown): Chain | null {
  const c = obj(raw);
  if (!c || typeof c.chainId !== 'string') return null;
  return {
    chainId:    c.chainId,
    trigger:    str(c.trigger) ?? 'SCHEDULER',
    actor:      str(c.actor),
    startedAt:  str(c.startedAt),
    finishedAt: str(c.finishedAt),
    status:     str(c.status) ?? 'RUNNING',
    steps:      Array.isArray(c.steps) ? c.steps.map(normalizeStep).filter((s): s is ChainStep => !!s) : [],
  };
}

/** daily-status — updateDone 이 boolean 이 아니면 계약이 아니라고 본다. */
export function normalizeDailyStatus(raw: unknown): DailyStatus | null {
  const d = obj(raw);
  if (!d || typeof d.updateDone !== 'boolean') return null;
  const r = obj(d.running);
  return {
    date:         str(d.date),
    updateDone:   d.updateDone,
    lastUpdateAt: str(d.lastUpdateAt),
    running:      r && typeof r.chainId === 'string'
      ? { chainId: r.chainId, trigger: str(r.trigger) ?? 'MANUAL', step: str(r.step) ?? '', jobId: str(r.jobId) }
      : null,
    chains:       Array.isArray(d.chains) ? d.chains.map(normalizeChain).filter((c): c is Chain => !!c) : [],
  };
}

export function normalizeMorningSummary(raw: unknown): MorningSummary | null {
  const m = obj(raw);
  if (!m || typeof m.ran !== 'boolean') return null;
  return { date: str(m.date), ran: m.ran, chain: normalizeChain(m.chain) };
}

const reviewState = (v: unknown): ReviewState => (v === 'KEEP' || v === 'APPLY' ? v : 'PENDING');
const riseReason = (v: unknown): RiseReason | null => (v === 'RATE_OVER_CAP' || v === 'OVER_MARKET_MAX' ? v : null);

/** drop-reviews 목록 — items 배열이 없으면 null. */
export function normalizeDropReviews(raw: unknown): DropReviewList | null {
  const d = obj(raw);
  if (!d || !Array.isArray(d.items)) return null;
  const items: DropReviewItem[] = d.items.map(obj).filter((i): i is Obj => !!i && typeof i.cardId === 'number').map((i) => ({
    cardId:            i.cardId as number,
    sellerProductCode: str(i.sellerProductCode),
    productName:       str(i.productName) ?? '',
    currentPrice:      typeof i.currentPrice === 'number' ? i.currentPrice : null,
    suggestedPrice:    typeof i.suggestedPrice === 'number' ? i.suggestedPrice : null,
    kind:              str(i.kind) ?? 'DROP',
    dropBucket:        (str(i.dropBucket) as DropReviewItem['dropBucket']) ?? null,
    dropSeverity:      (str(i.dropSeverity) as DropReviewItem['dropSeverity']) ?? null,
    marketStale:       i.marketStale === true,
    gated:             i.gated === true,
    reviewState:       reviewState(i.reviewState),
    reviewedAt:        str(i.reviewedAt),
    reviewedBy:        str(i.reviewedBy),
    riseReason:        riseReason(i.riseReason),
    marketMax:         typeof i.marketMax === 'number' && Number.isFinite(i.marketMax) ? i.marketMax : null,
    marketShopCount:   typeof i.marketShopCount === 'number' && Number.isFinite(i.marketShopCount) ? i.marketShopCount : null,
    marketCollectedAt: str(i.marketCollectedAt),
  }));
  return { items, pending: num(d.pending), keep: num(d.keep), apply: num(d.apply), gatedPending: num(d.gatedPending) };
}

export function normalizeDropReviewSave(raw: unknown): DropReviewSaveResult {
  const d = obj(raw) ?? {};
  const results = Array.isArray(d.results)
    ? d.results.map(obj).filter((r): r is Obj => !!r && typeof r.cardId === 'number').map((r) => ({
      cardId: r.cardId as number, ok: r.ok === true, reviewState: r.reviewState == null ? null : reviewState(r.reviewState), message: str(r.message),
    }))
    : [];
  return { results, saved: num(d.saved), rejected: num(d.rejected) };
}

/**
 * 서버 목록 → 하락 확인 대기 id(예전 localStorage pendingIds 와 같은 뜻) — 하락이고 검토 대기인 줄.
 * '참조 없음' 은 ③ 이 빼고 검토를 막지 않으므로(예전 분류 완료 때 대기에서 뺐다) 넣지 않는다. 상승 이상치는 하락 대기가 아니라
 * gatedRisePendingIds 로 따로.
 */
export function pendingDropIds(list: DropReviewList): number[] {
  return list.items
    .filter((i) => i.kind === 'DROP' && i.reviewState === 'PENDING' && i.dropBucket !== 'NO_REFERENCE')
    .map((i) => i.cardId);
}

/** ③ 이 APPLY 없이는 건너뛰는 상승 이상치(상승률 50% 초과 · 타 샵 최고가 초과 — riseReason) 중 검토 대기. */
export function gatedRisePendingIds(list: DropReviewList): number[] {
  return list.items.filter((i) => i.kind === 'RISE' && i.gated && i.reviewState === 'PENDING').map((i) => i.cardId);
}

/** 시세 수집이 며칠 전인가(오늘 0). 모르면 null. */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / 86_400_000));
}

/** ③ 결과 카드의 naverError 가 서버 검토 게이트의 건너뜀인가(실패가 아니라 검토가 필요한 것). */
export const isReviewGateSkip = (naverError: string | null | undefined): boolean =>
  !!naverError && /^건너뜀\s*—\s*(하락 검토 대기|상승 확인 필요)/.test(naverError);

/**
 * ① 완료 게이트(클라우드 리뷰 높음, 감사 card-controltower#656) — 서버 '오늘 받기 완료' 는 서버 검토 목록(하락 확인 대기)을 받은
 * 뒤에만 믿는다. 다른 PC 의 하락 확인 대기는 그 목록으로만 채워지므로, 목록 없이 서버 완료만 믿으면 ②③ 이 검토 없이 열린다.
 *   - updateDone: 이 브라우저의 완료 또는 (서버 완료 && 서버 검토 목록을 받음)
 *   - reviewListMissing: 서버는 완료인데 이 브라우저는 미완료이고 목록이 없다 — ③ 을 막고 목록을 다시 받게 한다
 */
export function dailyDoneGate(s: {
  localDone: boolean; serverDone: boolean; reviewSupported: boolean | null; reviewListLoaded: boolean;
}): { updateDone: boolean; reviewListMissing: boolean } {
  const ready = s.reviewSupported === true && s.reviewListLoaded;
  return {
    updateDone: s.localDone || (s.serverDone && ready),
    reviewListMissing: s.serverDone && !s.localDone && !ready,
  };
}
